import React, { Suspense, useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import TopHeader from "./TopHeader";
import TopMenuBar from "./TopMenuBar";
import PermissionGate from "@/components/auth/PermissionGate";
import { cn } from "@/lib/utils";

function PageLoader() {
  return (
    <div className="flex flex-1 items-center justify-center min-h-[200px]">
      <Loader2 className="h-8 w-8 animate-spin text-brand-primary" aria-hidden />
    </div>
  );
}

export default function AppShell() {
  const { pathname } = useLocation();

  const reducedMotion = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  const posFullscreen = pathname === "/pos";

  return (
    <PermissionGate>
      <div
        className={cn(
          "flex h-screen overflow-hidden",
          posFullscreen ? "bg-white" : "bg-transparent",
        )}
        data-testid="app-shell"
      >
        <div className="flex-1 flex flex-col min-w-0">
          {!posFullscreen && <TopMenuBar />}
          {!posFullscreen && <TopHeader />}
          <main
            className={cn(
              "flex-1 min-h-0 min-w-0",
              posFullscreen
                ? "overflow-hidden flex flex-col"
                : "overflow-x-hidden overflow-y-auto",
            )}
            data-testid="main-content"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={pathname}
                initial={reducedMotion ? false : { opacity: 0, y: 16, filter: "blur(4px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={reducedMotion ? undefined : { opacity: 0, y: -8 }}
                transition={{ duration: reducedMotion ? 0 : 0.32, ease: [0.22, 1, 0.36, 1] }}
                className={cn("min-h-0", posFullscreen ? "flex flex-1 flex-col h-full" : "min-h-full")}
              >
                <Suspense fallback={<PageLoader />}>
                  <Outlet />
                </Suspense>
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>
    </PermissionGate>
  );
}
